import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Coffee, ShoppingCart, Factory, HeartPulse, Utensils, SprayCan, HardHat, Baby, Boxes } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SEO } from "../components/SEO";
import { getIndustries } from "../lib/api";

const ICON = {
  coffee: Coffee, "shopping-cart": ShoppingCart, factory: Factory, "hard-hat": HardHat,
  "heart-pulse": HeartPulse, utensils: Utensils, "spray-can": SprayCan, baby: Baby,
};

export default function Industries() {
  const [items, setItems] = useState([]);
  useEffect(() => { window.scrollTo(0, 0); getIndustries().then(setItems).catch(() => {}); }, []);
  return (
    <main data-testid="industries-page" className="pt-[104px]">
      <SEO
        title="Industries We Supply | VR Packaging & Hygiene NZ"
        description="Tailored wholesale packaging and hygiene programmes for cafés, supermarkets, healthcare, hospitality, manufacturing, cleaning, construction and childcare centres across New Zealand."
        path="/industries"
      />
      <div className="bg-vr-navy text-white">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-14">
          <Breadcrumbs items={[{ label: "Industries" }]} />
          <h1 className="mt-6 font-display font-extrabold tracking-tight text-4xl md:text-6xl">Industries we supply</h1>
          <p className="mt-4 text-white/70 max-w-2xl">Tailored packaging and hygiene programmes for businesses across New Zealand — whatever you do, we keep you stocked.</p>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((ind, i) => {
            const Icon = ICON[ind.icon] || Boxes;
            return (
              <motion.div key={ind.slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}>
                <Link
                  to={`/industries/${ind.slug}`}
                  data-testid={`industry-${ind.slug}`}
                  className="group block rounded-3xl border border-vr-border bg-white overflow-hidden hover:border-vr-brand hover:shadow-2xl transition-all duration-300"
                >
                  {ind.image && (
                    <div className="relative aspect-[16/10] overflow-hidden bg-vr-bg2">
                      <img src={ind.image} alt={ind.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <span className="absolute top-4 left-4 grid place-items-center w-11 h-11 rounded-xl bg-white/95 text-vr-navy group-hover:bg-vr-brand group-hover:text-white transition-colors">
                        <Icon size={20} />
                      </span>
                    </div>
                  )}
                  <div className="p-7">
                    <h2 className="font-display font-bold text-xl text-vr-ink mb-2 group-hover:text-vr-brand transition-colors">{ind.name}</h2>
                    <p className="text-vr-dim leading-relaxed text-sm">{ind.desc}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-vr-navy font-semibold text-sm group-hover:text-vr-brand transition-colors">
                      Explore {ind.name} range <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-16 rounded-3xl bg-vr-bg2 border border-vr-border p-10 md:p-14 text-center">
          <h2 className="font-display font-extrabold text-2xl md:text-4xl text-vr-ink">Not sure what you need?</h2>
          <p className="mt-3 text-vr-dim max-w-xl mx-auto">Tell us about your operation and our team will build a tailored supply list with wholesale pricing.</p>
          <Link to="/contact" className="mt-7 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-vr-brand text-white font-semibold hover:bg-vr-branddark transition-colors">Request a Quote <ArrowRight size={18} /></Link>
        </div>
      </div>
    </main>
  );
}
